import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUnreadCount,
  listNotifications,
  markRead,
  markManyRead,
  markAllRead,
  deleteNotification,
  getPreferences,
  updatePreferences,
} from "../services/notificationApi";

export const useUnreadCount = (isAuthenticated) => {
  return useQuery({
    queryKey: ["notifications", "unreadCount"],
    queryFn: getUnreadCount,
    enabled: isAuthenticated,
    refetchInterval: 30000, // Poll every 30 seconds
    refetchIntervalInBackground: false, // Stop polling when tab inactive
  });
};

export const useNotifications = ({ unreadOnly = false, isAuthenticated }) => {
  return useInfiniteQuery({
    queryKey: ["notifications", { unreadOnly }],
    queryFn: ({ pageParam }) => listNotifications({ pageParam, limit: 20, unreadOnly }),
    getNextPageParam: (lastPage) => lastPage.meta?.nextCursor || undefined,
    enabled: isAuthenticated,
  });
};

export const useMarkRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => markRead(id),
    onMutate: async (id) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      const previousUnread = queryClient.getQueryData(["notifications", "unreadCount"]);
      if (previousUnread && previousUnread.count > 0) {
        queryClient.setQueryData(["notifications", "unreadCount"], { count: previousUnread.count - 1 });
      }

      // Update in lists
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((n) =>
              n.id === id ? { ...n, readAt: new Date().toISOString() } : n
            ),
          })),
        };
      });

      return { previousUnread };
    },
    onError: (err, id, context) => {
      if (context?.previousUnread) {
        queryClient.setQueryData(["notifications", "unreadCount"], context.previousUnread);
      }
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onSettled: () => {
      // Invalidate to ensure consistency, though optimistic update handles UI
      queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    },
  });
};

/**
 * Marks a batch of notifications read. Same optimistic shape as useMarkRead,
 * but decrements the badge by however many were actually still unread, so a
 * batch containing already-read rows cannot drive the counter negative.
 */
export const useMarkManyRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids) => markManyRead(ids),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      const idSet = new Set(ids);
      const previousUnread = queryClient.getQueryData(["notifications", "unreadCount"]);

      // Count only the ones the cache still believes are unread.
      let newlyRead = 0;
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((n) => {
              if (!idSet.has(n.id) || n.readAt) return n;
              newlyRead += 1;
              return { ...n, readAt: new Date().toISOString() };
            }),
          })),
        };
      });

      if (previousUnread?.count > 0 && newlyRead > 0) {
        queryClient.setQueryData(["notifications", "unreadCount"], {
          count: Math.max(0, previousUnread.count - newlyRead),
        });
      }

      return { previousUnread };
    },
    onError: (err, ids, context) => {
      if (context?.previousUnread) {
        queryClient.setQueryData(["notifications", "unreadCount"], context.previousUnread);
      }
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    },
  });
};

export const useMarkAllRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      queryClient.setQueryData(["notifications", "unreadCount"], { count: 0 });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => deleteNotification(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      // Snapshot every notifications query so a failed delete can put the
      // removed item back rather than leaving it gone until (and unless) the
      // onError invalidation's refetch succeeds.
      const previousQueries = queryClient.getQueriesData({ queryKey: ["notifications"] });

      // Remove from list optimistically
      queryClient.setQueriesData({ queryKey: ["notifications"] }, (old) => {
        if (!old || !old.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.filter((n) => n.id !== id),
          })),
        };
      });

      return { previousQueries };
    },
    onError: (err, id, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
    },
  });
};

export const useNotificationPreferences = (isAuthenticated) => {
  return useQuery({
    queryKey: ["notifications", "preferences"],
    queryFn: getPreferences,
    enabled: isAuthenticated,
  });
};

export const useUpdatePreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => updatePreferences(data),
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: ["notifications", "preferences"] });
      const previous = queryClient.getQueryData(["notifications", "preferences"]);
      
      queryClient.setQueryData(["notifications", "preferences"], (old) => {
        if (!old) return old;
        return { ...old, preferences: { ...old.preferences, ...newData } };
      });
      return { previous };
    },
    onError: (err, newData, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["notifications", "preferences"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", "preferences"] });
    },
  });
};
